import { useState } from "react";
import { useRouter } from "expo-router";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { CheckIcon } from "lucide-react-native";
import { useForm } from "react-hook-form";

import { ScreenLayout } from "@/blocks/ScreenLayout";
import { SecureTextEntryToggle } from "@/blocks/SecureTextEntryToggle";
import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnCard } from "@/components/VnCard";
import { VnForm } from "@/components/VnForm";
import { VnIcon } from "@/components/VnIcon";
import { VnText } from "@/components/VnText/VnText";
import { VnTextInput } from "@/components/VnTextInput/VnTextInput";
import { useUpdatePasswordMutation } from "@/data/mutations/authMutations";
import {
  createPasswordFormSchema,
  CreatePasswordFormType,
} from "@/domain/auth";
import { MFA_ENROLMENT_ROUTE } from "@/domain/constants";
import { useTranslator } from "@/hooks/useTranslate";
import { logger } from "@/utils/logger";

const CreatePasswordScreen = () => {
  const { t } = useTranslator();
  const router = useRouter();

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const { mutateAsync: updatePassword, isPending: isLoading } =
    useUpdatePasswordMutation();

  const createPasswordForm = useForm<CreatePasswordFormType>({
    defaultValues: { password: "" },
    resolver: standardSchemaResolver(createPasswordFormSchema),
  });

  const isPasswordValid = createPasswordForm.formState.isValid;

  const handleSubmit = async (data: CreatePasswordFormType) => {
    try {
      await updatePassword(data.password);
      router.replace(MFA_ENROLMENT_ROUTE);
    } catch (error) {
      logger.error({
        message: "Failed to create user with email and password",
        error,
      });
      createPasswordForm.setError("password", {
        message: t("common:validation.genericError"),
      });
    }
  };

  return (
    <ScreenLayout className="py-12">
      <VnBox className="mt-safe-offset-10 web:mt-10 flex items-center px-4 sm:my-auto md:justify-center">
        <VnBox className="w-full gap-4 md:w-3/6">
          <VnBox className="flex flex-col gap-6">
            <VnBox className="gap-2">
              <VnText
                variant="headerOne"
                i18nKey="native:createPasswordScreen.title"
                className="text-center"
              />
              <VnText
                variant="lg"
                className="text-muted-foreground text-center"
                i18nKey="native:createPasswordScreen.subtitle"
              />
            </VnBox>
            <VnForm form={createPasswordForm} isInputOnlyForm>
              <VnCard className="gap-6 rounded-2xl px-4 pb-[40px] pt-[20px]">
                <VnBox className="gap-3">
                  <VnTextInput
                    control={createPasswordForm.control}
                    name="password"
                    labelI18nKey="native:createPasswordScreen.passwordInputLabel"
                    placeholder={t(
                      "native:createPasswordScreen.passwordInputPlaceholder",
                    )}
                    autoCapitalize="none"
                    autoCorrect={false}
                    secureTextEntry={!isPasswordVisible}
                    returnKeyType="done"
                    enablesReturnKeyAutomatically
                    onSubmitEditing={() => {
                      void createPasswordForm.handleSubmit(handleSubmit)();
                    }}
                    isRequired
                    rightElement={
                      <SecureTextEntryToggle
                        isVisible={isPasswordVisible}
                        onPress={() => setIsPasswordVisible(prev => !prev)}
                      />
                    }
                  />
                  <VnBox className="flex-row items-center gap-2 px-1">
                    {isPasswordValid && (
                      <VnIcon
                        as={CheckIcon}
                        size={16}
                        className="text-green-600"
                      />
                    )}
                    <VnText
                      variant="sm"
                      className={
                        isPasswordValid
                          ? "text-green-600"
                          : "text-muted-foreground"
                      }
                      i18nKey="native:createPasswordScreen.passwordRequirement"
                    />
                  </VnBox>
                </VnBox>
                <VnButton
                  onPress={() => {
                    void createPasswordForm.handleSubmit(handleSubmit)();
                  }}
                  i18nKey="common:continue"
                  isLoading={isLoading}
                  className="rounded-xl"
                  disabled={!isPasswordValid}
                />
              </VnCard>
            </VnForm>
          </VnBox>
        </VnBox>
      </VnBox>
    </ScreenLayout>
  );
};

export { CreatePasswordScreen };
